/**
 * utils/dashboardParsers.js
 *
 * Funções puras para processar os dados brutos de métricas do Firebase
 * e isolar a lógica de cálculo do hook principal da página.
 */
import { pick } from "./metricHelpers";

export function parseFinancialData(state) {
    const { selectedDay = {}, selectedMonth = {}, previousMonth = {}, financialChartData = [] } = state;

    return {
        financialChartData,
        // DRE (Competência - Ocorreu a Venda)
        incomeMonth: pick(selectedMonth, "financial.income.gross"),
        previousIncomeMonth: pick(previousMonth, "financial.income.gross"),
        expenseMonth: pick(selectedMonth, "financial.expense.gross"),
        previousExpenseMonth: pick(previousMonth, "financial.expense.gross"),

        // Fluxo de Caixa MENSAL (Liquidez - O dinheiro mudou de mãos)
        paidIncomeMonth: pick(selectedMonth, "cashflow.in.paidGross"),
        previousPaidIncomeMonth: pick(previousMonth, "cashflow.in.paidGross"),
        paidIncomeMonthNet: pick(selectedMonth, "cashflow.in.paidNet"),
        previousPaidIncomeMonthNet: pick(previousMonth, "cashflow.in.paidNet"),
        pendingIncomeMonthNet: pick(selectedMonth, "cashflow.in.pendingNet"),
        previousPendingIncomeMonthNet: pick(previousMonth, "cashflow.in.pendingNet"),
        
        paidExpenseMonth: pick(selectedMonth, "cashflow.out.paidNet") || pick(selectedMonth, "cashflow.out.paidGross"),
        previousPaidExpenseMonth: pick(previousMonth, "cashflow.out.paidNet") || pick(previousMonth, "cashflow.out.paidGross"),

        // Fluxo de Caixa DIÁRIO (selectedDay = day.* do daily_YYYY-MM-DD)
        paidIncomeDayNet: pick(selectedDay, "cashflow.in.paidNet"),
        pendingIncomeDayNet: pick(selectedDay, "cashflow.in.pendingNet"),
        paidExpenseDay: pick(selectedDay, "cashflow.out.paidNet") || pick(selectedDay, "cashflow.out.paidGross"),
        incomeDayGross: pick(selectedDay, "financial.income.gross"),
        expenseDayGross: pick(selectedDay, "financial.expense.gross"),
    };
}

export function parseSalesData(state) {
    const { selectedDay = {}, selectedMonth = {}, previousMonth = {}, salesChartData = [] } = state;

    return {
        salesChartData,
        salesTodayCount: pick(selectedDay, "sales.count"),
        salesTodayGross: pick(selectedDay, "sales.gross"),
        salesMonthCount: pick(selectedMonth, "sales.count"),
        previousSalesMonthCount: pick(previousMonth, "sales.count"),
        salesMonthGross: pick(selectedMonth, "sales.gross"),
        previousSalesMonthGross: pick(previousMonth, "sales.gross"),
        salesMonthNet: pick(selectedMonth, "sales.net"),
        previousSalesMonthNet: pick(previousMonth, "sales.net"),
    };
}

export function parseMembersData(state, isToday) {
    const { selectedDay = {}, selectedMonth = {}, previousMonth = {}, snapshot = {}, previousSnapshot = {}, live = {}, renewalChartData = [] } = state;

    const liveSnapshotSource = isToday ? live : {};

    // Totalizadores estáticos (Se for hoje, tenta pegar do Live primeiro, senão usa Snapshot diário/mensal)
    const activeMembers = liveSnapshotSource.totalActiveMembers ?? snapshot.totalActiveMembers ?? 0;
    const activeMemberships = liveSnapshotSource.totalActiveMemberships ?? snapshot.totalActiveMemberships ?? 0;
    const inactiveMembers = liveSnapshotSource.totalInactiveMembers ?? snapshot.totalInactiveMembers ?? 0;
    const leads = liveSnapshotSource.totalLeads ?? snapshot.totalLeads ?? 0;
    
    const currentMRR = liveSnapshotSource.currentMRR ?? snapshot.currentMRR ?? 0;
    const payingMemberships = liveSnapshotSource.totalPayingMemberships ?? snapshot.totalPayingMemberships ?? 0;
    const refDivider = payingMemberships > 0 ? payingMemberships : 1;
    const ticketMedio = currentMRR > 0 ? (currentMRR / refDivider) : 0;

    const growthChartData = [...(state.growthChartData || [])];
    if (isToday && growthChartData.length > 0) {
        const lastIdx = growthChartData.length - 1;
        growthChartData[lastIdx] = {
            ...growthChartData[lastIdx],
            currentActive: activeMembers,
            ticket: ticketMedio,
            mrr: currentMRR,
        };
    }

    const cancellationsMonth = pick(selectedMonth, "members.cancellations");

    return {
        activeMembers,
        previousActiveMembers: previousSnapshot.totalActiveMembers ?? 0,
        inactiveMembers,
        previousInactiveMembers: previousSnapshot.totalInactiveMembers ?? 0,
        leads,
        previousLeads: previousSnapshot.totalLeads ?? 0,
        activeMemberships,
        previousActiveMemberships: previousSnapshot.totalActiveMemberships ?? 0,
        
        ticketMedio,
        previousTicketMedio: previousSnapshot.ticketMedio || 0,
        currentMRR,
        previousMRR: previousSnapshot.currentMRR || 0,

        renewalChartData,
        growthChartData,

        newMembersToday: pick(selectedDay, "members.newMembers"),
        renewalsToday: pick(selectedDay, "members.renewals"),
        returnsToday: pick(selectedDay, "members.returns"),
        newMembersMonth: pick(selectedMonth, "members.newMembers"),
        previousNewMembersMonth: pick(previousMonth, "members.newMembers"),
        renewalsMonth: pick(selectedMonth, "members.renewals"),
        previousRenewalsMonth: pick(previousMonth, "members.renewals"),
        returnsMonth: pick(selectedMonth, "members.returns"),
        previousReturnsMonth: pick(previousMonth, "members.returns"),
        activationsMonth: pick(selectedMonth, "members.activationsTotal"),
        previousActivationsMonth: pick(previousMonth, "members.activationsTotal"),
        cancellationsMonth,
        previousCancellationsMonth: pick(previousMonth, "members.cancellations"),
        churnRate: activeMembers > 0 ? ((cancellationsMonth / activeMembers) * 100) : 0,
        previousChurnRate: (previousSnapshot.totalActiveMembers || 0) > 0
            ? ((pick(previousMonth, "members.cancellations") / (previousSnapshot.totalActiveMembers || 1)) * 100)
            : 0,
    };
}

export function parseOccupancyData(state, isToday) {
    const { snapshot = {}, live = {}, occupancyHistory = [] } = state;
    
    const activitySource = isToday ? (live.activityMembers || {}) : (snapshot.activityMembers || {});
    const occupancySource = isToday ? (live.occupancy || snapshot.occupancy || {}) : (snapshot.occupancy || {});
    
    const history = [...occupancyHistory];
    if (isToday && history.length > 0) {
        const liveOcc = live.occupancy || {};
        const liveCap = liveOcc.totalCapacity || 0;
        const liveEnr = liveOcc.totalEnrolled || 0;
        if (liveCap > 0 || liveEnr > 0) {
            const currentMonthIdx = new Date().getMonth();
            if (currentMonthIdx >= 0 && currentMonthIdx < history.length) {
                history[currentMonthIdx] = {
                    ...history[currentMonthIdx],
                    capacity: liveCap,
                    enrolled: liveEnr,
                    pct: liveCap > 0 ? Math.round((liveEnr / liveCap) * 100) : 0,
                };
            }
        }
    }

    return {
        activityChartData: Object.values(activitySource)
            .map((activity) => ({
                name: activity.name || "Sem nome",
                color: activity.color || "#3b82f6",
                count: activity.count || 0,
            }))
            .filter((activity) => activity.count > 0)
            .sort((a, b) => b.count - a.count),
        occupancy: {
            totalSpots: occupancySource.totalCapacity || 0,
            occupiedSpots: occupancySource.totalEnrolled || 0,
            activities: occupancySource.activities || {},
            history,
        }
    };
}

/**
 * Helpers internos para substituir o mês atual nos gráficos pelo MTD selecionado.
 * Quando o usuário está vendo um dia passado, a barra do mês corrente no gráfico
 * deve refletir o acumulado até aquele dia, não o mês inteiro.
 */
function resolveMonthData(monthISO, monthlyMap, selectedMonthISO, selectedMonthData) {
    if (selectedMonthData && monthISO === selectedMonthISO) {
        return selectedMonthData;
    }
    return monthlyMap[monthISO] || {};
}

export function buildFinancialChartData(trailingMonths, monthlyMap, selectedMonthISO, selectedMonthData) {
    return trailingMonths.map(({ monthISO, label }) => {
        const monthData = resolveMonthData(monthISO, monthlyMap, selectedMonthISO, selectedMonthData);

        return {
            month: label.substring(0, 3),
            income: pick(monthData, "financial.income.gross"),
            expense: pick(monthData, "financial.expense.gross"),
            paidIncome: pick(monthData, "cashflow.in.paidNet"),
            paidExpense: pick(monthData, "cashflow.out.paidNet"),
            net: pick(monthData, "financial.income.gross") - pick(monthData, "financial.expense.gross"),
            paidNet: pick(monthData, "cashflow.in.paidNet") - pick(monthData, "cashflow.out.paidNet"),
        };
    });
}

export function buildRenewalChartData(currentYearMonths, previousYearMonths, monthlyMap, selectedMonthISO, selectedMonthData) {
    return currentYearMonths.map(({ monthISO, label }, index) => {
        const currentData = resolveMonthData(monthISO, monthlyMap, selectedMonthISO, selectedMonthData);
        const previousData = monthlyMap[previousYearMonths[index].monthISO] || {};

        return {
            month: label,
            current: pick(currentData, "members.renewals"),
            previous: pick(previousData, "members.renewals"),
        };
    });
}

export function buildGrowthChartData(currentYearMonths, previousYearMonths, monthlyMap, selectedMonthISO, selectedMonthData) {
    return currentYearMonths.map(({ monthISO, label }, index) => {
        const currentData = resolveMonthData(monthISO, monthlyMap, selectedMonthISO, selectedMonthData);
        const previousData = monthlyMap[previousYearMonths[index].monthISO] || {};

        return {
            month: label.substring(0, 3),
            currentActive: currentData.snapshot?.totalActiveMembers || 0,
            prevActive: previousData.snapshot?.totalActiveMembers || 0,
            ticket: currentData.snapshot?.ticketMedio || 0,
            mrr: currentData.snapshot?.currentMRR || 0,
        };
    });
}

export function buildOccupancyHistory(currentYearMonths, previousYearMonths, monthlyMap) {
    return currentYearMonths.map(({ monthISO, label }, index) => {
        const currentOcc = monthlyMap[monthISO]?.snapshot?.occupancy || {};
        const prevMonthISO = previousYearMonths[index]?.monthISO;
        const prevOcc = prevMonthISO ? (monthlyMap[prevMonthISO]?.snapshot?.occupancy || {}) : {};

        const capacity = currentOcc.totalCapacity || 0;
        const enrolled = currentOcc.totalEnrolled || 0;
        const prevCapacity = prevOcc.totalCapacity || 0;
        const prevEnrolled = prevOcc.totalEnrolled || 0;

        return {
            month: label.substring(0, 3),
            capacity,
            enrolled,
            pct: capacity > 0 ? Math.round((enrolled / capacity) * 100) : 0,
            prevCapacity,
            prevEnrolled,
            prevPct: prevCapacity > 0 ? Math.round((prevEnrolled / prevCapacity) * 100) : 0,
        };
    });
}

export function buildSalesChartData(currentYearMonths, previousYearMonths, monthlyMap, selectedMonthISO, selectedMonthData) {
    return currentYearMonths.map(({ monthISO, label }, index) => {
        const currentData = resolveMonthData(monthISO, monthlyMap, selectedMonthISO, selectedMonthData);
        const previousData = monthlyMap[previousYearMonths[index].monthISO] || {};

        return {
            month: label.substring(0, 3).toUpperCase(),
            currentGross: pick(currentData, "sales.gross"),
            previousGross: pick(previousData, "sales.gross"),
            currentCount: pick(currentData, "sales.count"),
            previousCount: pick(previousData, "sales.count"),
        };
    });
}

export function parseAttendanceData(state) {
    const { selectedMonth = {}, previousMonth = {}, snapshot = {}, previousSnapshot = {} } = state;

    const attendance = selectedMonth.attendance || snapshot.attendance || {};
    const prevAttendance = previousMonth.attendance || previousSnapshot.attendance || {};

    return {
        attendanceRate: attendance.rate || 0,
        previousAttendanceRate: prevAttendance.rate || 0,
        attendanceTotalPresent: attendance.totalPresent || 0,
        previousAttendanceTotalPresent: prevAttendance.totalPresent || 0,
        attendanceTotalAbsent: attendance.totalAbsent || 0,
        previousAttendanceTotalAbsent: prevAttendance.totalAbsent || 0,
        attendanceTotalEnrolled: attendance.totalEnrolled || 0,
        previousAttendanceTotalEnrolled: prevAttendance.totalEnrolled || 0,
        attendanceTotalSessions: attendance.totalSessions || 0,
        previousAttendanceTotalSessions: prevAttendance.totalSessions || 0,
        attendanceByEmployee: attendance.byEmployee || {},
    };
}

export function buildAttendanceChartData(currentYearMonths, previousYearMonths, monthlyMap, selectedMonthISO, selectedMonthData) {
    return currentYearMonths.map(({ monthISO, label }, index) => {
        const currentData = resolveMonthData(monthISO, monthlyMap, selectedMonthISO, selectedMonthData);
        const previousData = monthlyMap[previousYearMonths[index].monthISO] || {};

        const currentAtt = currentData.attendance || currentData.snapshot?.attendance || {};
        const prevAtt = previousData.attendance || previousData.snapshot?.attendance || {};

        return {
            month: label.substring(0, 3),
            currentRate: currentAtt.rate || 0,
            prevRate: prevAtt.rate || 0,
            enrolled: currentAtt.totalEnrolled || 0,
            present: currentAtt.totalPresent || 0,
            absent: currentAtt.totalAbsent || 0,
            sessions: currentAtt.totalSessions || 0,
        };
    });
}
